import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Box
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon
} from '@mui/icons-material';
import ApiService from '../services/apiService';

// Komponente wurde überarbeitet, um sowohl als eigenständige Seite als auch als Komponente in Settings verwendet werden zu können
const CompetitorsList = ({ embedded = false }) => {
  // Zustand für die Liste der Wettbewerber
  const [competitors, setCompetitors] = useState([]);
  
  // Zustand für den Dialog zum Löschen eines Wettbewerbers
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    competitorId: null,
    competitorName: ''
  });
  
  // Zustand für die Snackbar-Benachrichtigungen
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Zustand für den Konkurrenten, der gelöscht werden soll
  const [competitorToDelete, setCompetitorToDelete] = useState(null);
  // Zustand für den Dialog
  const [dialogOpen, setDialogOpen] = useState(false);

  // Laden der Wettbewerber beim Start
  useEffect(() => {
    loadCompetitors();
  }, []);

  // Funktion zum Laden aller Wettbewerber
  const loadCompetitors = async () => {
    try {
      const competitors = await ApiService.getCompetitors();
      const detailedCompetitors = await Promise.all(
        competitors.map(async competitor => {
          const details = await ApiService.getCompetitorById(competitor.id);
          return details || competitor;
        })
      );
      setCompetitors(detailedCompetitors);
    } catch (error) {
      console.error('Fehler beim Laden der Wettbewerber:', error);
    }
  };
  
  // Dialog zum Löschen eines Wettbewerbers öffnen
  const openDeleteDialog = (id, name) => {
    setDeleteDialog({
      open: true,
      competitorId: id,
      competitorName: name
    });
  };
  
  // Dialog zum Löschen eines Wettbewerbers schließen
  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      competitorId: null,
      competitorName: ''
    });
  };
  
  // Wettbewerber löschen
  const handleDeleteCompetitor = async () => {
    if (!competitorToDelete) return;

    const competitorId = competitorToDelete.id;
    
    try {
      await ApiService.deleteCompetitor(competitorId);
      // Wettbewerber aus der Liste entfernen
      setCompetitors(prev => prev.filter(competitor => competitor.id !== competitorId));
      setSnackbar({
        open: true,
        message: 'Wettbewerber erfolgreich gelöscht',
        severity: 'success'
      });
      closeDeleteDialog();
    } catch (error) {
      console.error('Fehler beim Löschen des Wettbewerbers:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Löschen des Wettbewerbers',
        severity: 'error'
      });
      closeDeleteDialog();
    }
  };
  
  // Handler für das Schließen der Snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Erstellt die Tabelle mit den Wettbewerbern
  const renderCompetitorsTable = () => (
    <Paper elevation={embedded ? 2 : 3} sx={embedded ? { mt: 3 } : undefined}>
      <Box p={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Logo</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Pakete</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {competitors.length > 0 ? (
                competitors.map((competitor) => (
                  <TableRow key={competitor.id}>
                    <TableCell>
                      {competitor.logo_path ? (
                        <img 
                          src={competitor.logo_path} 
                          alt={`${competitor.name} Logo`} 
                          style={{ width: 40, height: 40 }} 
                        />
                      ) : (
                        "Kein Logo"
                      )}
                    </TableCell>
                    <TableCell>{competitor.name}</TableCell>
                    <TableCell>
                      {competitor.packages && competitor.packages.length > 0 ? (
                        <ul style={{ margin: 0, paddingInlineStart: '20px' }}>
                          {competitor.packages.map(pkg => (
                            <li key={pkg.id}>
                              {pkg.name} -                              {pkg.is_flatrate 
                                ? `Flatrate: ${pkg.flatrate_amount} €` 
                                : `${pkg.percentage_fee}% + ${pkg.fixed_fee} €`}
                              {pkg.is_tax_inclusive !== undefined && 
                                ` ${pkg.is_tax_inclusive ? "(inkl. MwSt.)" : "(zzgl. 19% MwSt.)"}`}
                            {(pkg.min_ticket_price || pkg.max_ticket_price) && " ("}
                            {pkg.min_ticket_price && `min: ${pkg.min_ticket_price} €`}
                            {pkg.min_ticket_price && pkg.max_ticket_price && ", "}
                            {pkg.max_ticket_price && `max: ${pkg.max_ticket_price} €`}
                            {(pkg.min_ticket_price || pkg.max_ticket_price) && ")"}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        "Keine Pakete"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        color="primary"
                        component={Link}
                        to={`/competitors/edit/${competitor.id}`}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error"
                        onClick={() => openDeleteDialog(competitor.id, competitor.name)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      Keine Wettbewerber vorhanden
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );

  return (
    <>
      {/* Wenn nicht eingebettet, zeige Überschrift und Button an */}
      {!embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Wettbewerber
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={Link}
            to="/competitors/new"
          >
            Neuer Wettbewerber
          </Button>
        </Box>
      )}

      {/* Wettbewerbertabelle */}
      {renderCompetitorsTable()}

      {/* Dialog zum Bestätigen des Löschens */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
      >
        <DialogTitle>Wettbewerber löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie den Wettbewerber "{deleteDialog.competitorName}" löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Abbrechen</Button>
          <Button onClick={handleDeleteCompetitor} color="error" autoFocus>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Benachrichtigungen */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CompetitorsList;